import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  RefreshCw,
  Trash2,
  Play,
  HardDrive,
  Database,
  Users,
  Store
} from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

const STAGES = {
  'queued': { label: 'Queued', color: 'bg-gray-500' },
  'counting_rows': { label: 'Counting Rows', color: 'bg-blue-500' },
  'processing': { label: 'Processing Data', color: 'bg-blue-500' },
  'inserting_customers': { label: 'Inserting Customers', color: 'bg-purple-500' },
  'inserting_stores': { label: 'Inserting Stores', color: 'bg-purple-500' },
  'updating_stats': { label: 'Updating Statistics', color: 'bg-green-500' },
  'completed': { label: 'Completed', color: 'bg-green-600' },
  'failed': { label: 'Failed', color: 'bg-red-500' },
  'cancelled': { label: 'Cancelled', color: 'bg-orange-500' }
};

export default function CSVUploadProgress() {
  const { isSuperAdmin } = useAuth();
  const { brands } = useChat();
  const fileInputRef = useRef(null);
  
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('sales');
  const [mode, setMode] = useState('append');
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  const selectedBrand = brands.find(b => String(b.brand_id) === selectedBrandId);

  // Fetch jobs for selected brand
  const fetchJobs = useCallback(async () => {
    if (!selectedBrandId) return;
    
    setLoadingJobs(true);
    try {
      const response = await fetch(`${API}/api/settings/csv-jobs/${selectedBrandId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  }, [selectedBrandId]);

  // Poll for job updates when there are active jobs
  useEffect(() => {
    if (!selectedBrandId) return;
    
    fetchJobs();
    
    // Poll every 2 seconds if there are active jobs
    const hasActiveJobs = jobs.some(j => ['queued', 'processing', 'counting_rows', 'inserting_customers', 'inserting_stores', 'updating_stats'].includes(j.status));
    
    if (hasActiveJobs) {
      const interval = setInterval(fetchJobs, 2000);
      return () => clearInterval(interval);
    }
  }, [selectedBrandId, jobs.length]);

  // Initial fetch when brand changes
  useEffect(() => {
    if (selectedBrandId) {
      fetchJobs();
    }
  }, [selectedBrandId, fetchJobs]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return new Date(timestamp).toLocaleString('en-IN', {
        dateStyle: 'short',
        timeStyle: 'short'
      });
    } catch {
      return timestamp;
    }
  };

  // Upload progress state for chunked uploads
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Chunked upload for large files (>5MB) with retry logic
  const uploadInChunks = async (file, brandId) => {
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks (smaller for better reliability)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const MAX_RETRIES = 5;
    const RETRY_DELAY = 2000; // 2 seconds between retries
    
    setUploadProgress(0);
    let serverUploadId = uploadId;
    
    // Helper to delay between retries
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    // Upload a single chunk with retry logic
    const uploadChunkWithRetry = async (chunkIndex, base64Chunk) => {
      let lastError = null;
      
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min timeout per chunk
          
          const response = await fetch(`${API}/api/settings/csv-upload-base64/${brandId}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
              upload_id: chunkIndex === 0 ? '' : serverUploadId,
              filename: file.name,
              chunk_data: base64Chunk,
              chunk_index: chunkIndex,
              total_chunks: totalChunks,
              file_type: fileType,
              mode: mode
            })
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Server error: ${response.status} - ${errorText}`);
          }
          
          const data = await response.json();
          
          if (!data.success) {
            // Check for missing chunks error - don't retry, throw immediately
            if (data.missing_chunks) {
              throw new Error(`Missing chunks: ${data.missing_chunks.join(', ')}`);
            }
            throw new Error(data.error || data.detail || 'Upload failed');
          }
          
          // Get upload_id from first chunk response
          if (chunkIndex === 0 && data.upload_id) {
            serverUploadId = data.upload_id;
          }
          
          return data;
        } catch (error) {
          lastError = error;
          
          // Don't retry on abort or missing chunks
          if (error.name === 'AbortError' || error.message.includes('Missing chunks')) {
            throw error;
          }
          
          if (attempt < MAX_RETRIES - 1) {
            console.log(`Chunk ${chunkIndex} failed (attempt ${attempt + 1}/${MAX_RETRIES}): ${error.message}. Retrying...`);
            await delay(RETRY_DELAY * (attempt + 1)); // Exponential backoff
          }
        }
      }
      
      throw new Error(`Chunk ${chunkIndex} failed after ${MAX_RETRIES} attempts: ${lastError?.message}`);
    };
    
    // Process chunks sequentially for reliability
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      // Convert chunk to base64
      const base64Chunk = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1]; // Remove data URL prefix
          resolve(base64);
        };
        reader.onerror = () => reject(new Error('Failed to read file chunk'));
        reader.readAsDataURL(chunk);
      });
      
      const data = await uploadChunkWithRetry(chunkIndex, base64Chunk);
      
      // Update progress
      const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
      setUploadProgress(progress);
      
      // If this was the last chunk, return the job_id
      if (data.job_id) {
        return data;
      }
    }
    
    throw new Error('Upload completed but no job_id received');
  };
  
  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !selectedBrandId) {
      toast.error('Please select a brand and file');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const LARGE_FILE_THRESHOLD = 5 * 1024 * 1024; // 5MB - use chunks for any moderately large file
      
      let data;
      
      if (selectedFile.size > LARGE_FILE_THRESHOLD) {
        // Use chunked upload for large files
        const sizeStr = formatSize(selectedFile.size);
        const chunks = Math.ceil(selectedFile.size / (2 * 1024 * 1024));
        toast.info(`Uploading ${sizeStr} in ${chunks} chunks...`);
        data = await uploadInChunks(selectedFile, selectedBrandId);
      } else {
        // Use direct upload for smaller files
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const response = await fetch(
          `${API}/api/settings/csv-upload-async/${selectedBrandId}?file_type=${fileType}&mode=${mode}`,
          {
            method: 'POST',
            credentials: 'include',
            body: formData
          }
        );
        
        data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || data.error || 'Upload failed');
        }
      }
      
      if (data.success) {
        toast.success(`File uploaded! Processing started with job ID: ${data.job_id.slice(0, 8)}...`);
        setSelectedFile(null);
        setUploadProgress(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchJobs();
      } else {
        toast.error(`Upload failed: ${data.detail || data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error.message || 'Please try again.'}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Cancel job
  const handleCancel = async (jobId) => {
    try {
      const response = await fetch(`${API}/api/settings/csv-job/${jobId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Job cancelled');
        fetchJobs();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to cancel job');
      }
    } catch (error) {
      toast.error('Error cancelling job');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6 text-center text-gray-400">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <p>Only Super Admins can access CSV Upload</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="csv-upload-progress">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Upload className="w-7 h-7 text-blue-400" />
            CSV Data Upload
          </h1>
          <p className="text-gray-400 mt-1">
            Upload large CSV files with async background processing
          </p>
        </div>
      </div>

      {/* Upload Form */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg text-white">New Upload</CardTitle>
          <CardDescription>
            Select a brand and file to upload. Large files are supported.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Brand Selection */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Brand</label>
              <Select value={selectedBrandId} onValueChange={setSelectedBrandId}>
                <SelectTrigger className="bg-gray-900 border-gray-700">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((brand) => (
                    <SelectItem key={brand.brand_id} value={String(brand.brand_id)}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Type */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">File Type</label>
              <Select value={fileType} onValueChange={setFileType}>
                <SelectTrigger className="bg-gray-900 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales Data</SelectItem>
                  <SelectItem value="returns">Returns Data</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Mode */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Mode</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="bg-gray-900 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">Append (Add to existing)</SelectItem>
                  <SelectItem value="replace">Replace (Clear first)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* File Input */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">CSV File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
              />
            </div>
          </div>

          {/* Selected File Info */}
          {selectedFile && (
            <div className="flex items-center gap-3 p-3 bg-gray-900/50 rounded-lg">
              <FileSpreadsheet className="w-8 h-8 text-green-400" />
              <div className="flex-1">
                <p className="text-white font-medium">{selectedFile.name}</p>
                <p className="text-sm text-gray-400">
                  {formatSize(selectedFile.size)}
                  {selectedFile.size > 50 * 1024 * 1024 && (
                    <span className="ml-2 text-yellow-400">(Large file - will use chunked upload)</span>
                  )}
                </p>
                {/* Upload Progress Bar */}
                {uploading && uploadProgress > 0 && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Uploading: {uploadProgress}%</p>
                  </div>
                )}
              </div>
              <Button
                onClick={handleUpload}
                disabled={uploading || !selectedBrandId}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="start-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploadProgress > 0 ? `${uploadProgress}%` : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Start Upload
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processing Jobs */}
      {selectedBrandId && (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-white">Processing Jobs</CardTitle>
                <CardDescription>
                  Jobs for {selectedBrand?.name || selectedBrandId}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchJobs}
                disabled={loadingJobs}
                className="border-gray-600"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingJobs ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingJobs && jobs.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Database className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No processing jobs found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {jobs.map((job) => {
                  const stage = STAGES[job.status] || STAGES[job.stage] || STAGES['queued'];
                  const isActive = ['queued', 'processing', 'counting_rows', 'inserting_customers', 'inserting_stores', 'updating_stats'].includes(job.status);
                  
                  return (
                    <div 
                      key={job.job_id}
                      className={`p-4 rounded-lg border ${
                        job.status === 'completed' ? 'bg-green-900/10 border-green-800' :
                        job.status === 'failed' ? 'bg-red-900/10 border-red-800' :
                        isActive ? 'bg-blue-900/10 border-blue-800' :
                        'bg-gray-900/50 border-gray-700'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-gray-400" />
                            <span className="text-white font-medium">{job.filename}</span>
                            <Badge className={stage.color}>
                              {stage.label}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                            <span>{formatSize(job.file_size)}</span>
                            <span>{job.file_type} ({job.mode})</span>
                            <span>Started: {formatTime(job.created_at)}</span>
                          </div>
                        </div>
                        
                        {isActive && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(job.job_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      {/* Progress Bar */}
                      {isActive && (
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-400">
                              {job.stage === 'processing' ? 
                                `Processing rows: ${job.rows_processed?.toLocaleString() || 0} / ${job.total_rows?.toLocaleString() || '?'}` :
                                stage.label
                              }
                            </span>
                            <span className="text-sm text-white">{job.progress_percent || 0}%</span>
                          </div>
                          <Progress value={job.progress_percent || 0} className="h-2" />
                        </div>
                      )}

                      {/* Result Summary */}
                      {job.status === 'completed' && job.result && (
                        <div className="grid grid-cols-4 gap-4 mt-3 pt-3 border-t border-gray-700">
                          <div className="text-center">
                            <p className="text-xl font-bold text-white">
                              {job.result.total_rows?.toLocaleString() || 0}
                            </p>
                            <p className="text-xs text-gray-400">Rows Processed</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-green-400">
                              {job.result.customers_created?.toLocaleString() || 0}
                            </p>
                            <p className="text-xs text-gray-400">Customers</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-blue-400">
                              {job.result.stores_created?.toLocaleString() || 0}
                            </p>
                            <p className="text-xs text-gray-400">Stores</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-purple-400">
                              ₹{(job.result.total_revenue / 10000000)?.toFixed(1) || 0}Cr
                            </p>
                            <p className="text-xs text-gray-400">Revenue</p>
                          </div>
                        </div>
                      )}

                      {/* Error Message */}
                      {job.status === 'failed' && job.error && (
                        <div className="mt-2 p-2 bg-red-900/20 rounded text-sm text-red-300">
                          Error: {job.error}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
